import {
  Button,
  ButtonSize,
  ButtonVariant,
  Column,
  DatePicker,
  Divider,
  FileList,
  InputFileDropdown,
  Loader,
  Row,
  RowSize,
  ValidationWrapper,
} from "@/components";
import { FileProps } from "@/components/file-list/types";
import { UserInputValue } from "@/components/input-file/types";
import { InputValidated } from "@/components/input-validated";
import { DEFAULT_DATE_FORMAT, DocTypeIds } from "@/constants";
import { AuthContext } from "@/guards/auth/auth-context";
import { useRequestState } from "@/hooks";
import { useOperationsWithAttachments } from "@/pages/contractor-profile/hooks/use-operations-with-attachments";
import { useSubcontractorFormOperations } from "@/pages/contractor-profile/hooks/use-subcontractor-form-operations";
import {
  SubContractor,
  requestDocumentBySubcontractorId,
  requestSubcontractor,
} from "@/services/api";
import { selectUserData, useBoundStore } from "@/services/stores";
import { logger } from "@/utils";
import { checkIfStringAndDash } from "@/utils/check-if-string-and-dash";
import { getValidDate } from "@/utils/get-valid-date";
import { zodResolver } from "@hookform/resolvers/zod";
import { compact, size } from "lodash";
import { FC, useCallback, useContext, useEffect } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { SubcontractorSchema } from "./schema";
import styles from "./styles.scss";
import {
  SubcontractorField,
  SubcontractorFieldLabel,
  SubcontractorFieldSubLabel,
} from "./types";
import { checkIfStringDashPlus } from "@/utils/check-if-string-dash-plus";

export const SubcontractorForm: FC = () => {
  const { account } = useContext(AuthContext);
  const { user } = useBoundStore(selectUserData);
  const { id } = useParams();

  const { data, setSuccess, setError, isLoading } =
    useRequestState<SubContractor>({
      isLoading: true,
    });

  const resolver = zodResolver(SubcontractorSchema(data?.AdnocId));
  const isDisabledForm = !!data?.AdnocId;
  const isEdit = !!id;

  const {
    clearErrors,
    setValue,
    getValues,
    formState: { errors, isValid },
    handleSubmit,
    trigger,
  } = useForm<FieldValues>({
    resolver,
    defaultValues: {
      [SubcontractorField.ARABIC_NAME]: "",
      [SubcontractorField.ENGLISH_NAME]: "",
      [SubcontractorField.CICPA_ID]: "",
      [SubcontractorField.PHONE]: "",
      [SubcontractorField.ADDCI]: "",
    },
  });

  useEffect(() => {
    if (!id) {
      return;
    }

    const controller = new AbortController();

    const subcontractorPromise = requestSubcontractor(
      { email: account?.username ?? "" },
      { signal: controller.signal },
    ).then(
      response =>
        response.find(item => item.SubContractorId === Number(id)) || null,
    );

    const docsPromise = requestDocumentBySubcontractorId(
      { subcontractorId: id },
      { signal: controller.signal },
    ).catch(() => {
      const error = "Can't find subcontractor document";
      logger.error(error);
    });

    Promise.all([subcontractorPromise, docsPromise])
      .then(([subcontractor, document]) => {
        if (!subcontractor) {
          const error = "Can't find subcontractor with this id";
          setError(error);
          logger.error(error);

          return;
        }

        setSuccess({
          ...subcontractor,
          TradeLicenseDocument: document ?? {
            FileBase64: "",
          },
        });
      })
      .catch(err => {
        setError(err);
        logger.error(err);
      });

    return () => {
      controller.abort();
    };
  }, [account, id, setError, setSuccess]);

  const handleDataChange = useCallback(
    (value: UserInputValue, dropdownId: SubcontractorField) => {
      clearErrors(dropdownId);
      setValue(dropdownId, value, { shouldValidate: true });
    },
    [clearErrors, setValue],
  );

  useEffect(() => {
    if (data) {
      const {
        AdnocId,
        AdcciMembershipNo,
        CompanyNameAr,
        CompanyNameEn,
        SubContractorCicpaId,
        CompanyNo,
        PhoneNumber,
        TradeLicenseExpiryDate,
        TradeLicenseDocument,
      } = data;

      handleDataChange(AdnocId, SubcontractorField.ID);
      handleDataChange(
        CompanyNo?.toString() ?? "",
        SubcontractorField.COMPANY_ID,
      );
      handleDataChange(CompanyNameAr ?? "", SubcontractorField.ARABIC_NAME);
      handleDataChange(CompanyNameEn ?? "", SubcontractorField.ENGLISH_NAME);
      handleDataChange(
        SubContractorCicpaId?.toString() ?? "",
        SubcontractorField.CICPA_ID,
      );
      handleDataChange(PhoneNumber?.toString() ?? "", SubcontractorField.PHONE);
      handleDataChange(AdnocId ?? "", SubcontractorField.UNIFIED_ID);
      handleDataChange(AdcciMembershipNo ?? "", SubcontractorField.ADDCI);

      handleDataChange(
        // TODO Fix types
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        getValidDate(TradeLicenseExpiryDate),
        SubcontractorField.TRADE_LICENSE_EXPIRY,
      );
      handleDataChange(
        TradeLicenseDocument?.FileBase64 ?? "",
        SubcontractorField.TRADE_LICENSE,
      );
      handleDataChange(
        TradeLicenseDocument?.Format ?? "",
        SubcontractorField.TRADE_LICENSE_FILE_FORMAT,
      );
      handleDataChange(
        TradeLicenseDocument?.DocNameEn ?? "",
        SubcontractorField.TRADE_LICENSE_FILE_NAME,
      );
    }
  }, [data, handleDataChange]);

  const { isSubmitting, onSubmit, onDelete, onCancel } =
    useSubcontractorFormOperations({
      getValues,
      email: data?.Email ?? user?.Email ?? "",
      ownCompanyNo: user?.Contractor?.CompanyNo ?? "",
      contractorId: data?.ContractorId ?? user?.Contractor?.ContractorId ?? "",
      adcciMembershipNo:
        data?.AdcciMembershipNo ?? user?.Contractor?.AdcciMembershipNo ?? "",
      docTypeId:
        data?.TradeLicenseDocument?.DocTypeId ?? DocTypeIds.Subcontractor,
    });

  const { onUploadDocument, onRemoveDocument } = useOperationsWithAttachments({
    getValues,
    setValue,
    clearErrors,
    trigger,
  });

  const handleRemoveDocument = () => {
    onRemoveDocument(SubcontractorField.TRADE_LICENSE);
  };

  const attachment: FileProps[] = getValues([SubcontractorField.TRADE_LICENSE]);

  const attachmentName: string[] = getValues([
    SubcontractorField.TRADE_LICENSE_FILE_NAME,
  ]);

  const attachmentFormat: string[] = getValues([
    SubcontractorField.TRADE_LICENSE_FILE_FORMAT,
  ]);

  if (id && isLoading) {
    return (
      <div className={styles.pageWrapper} style={{ height: "100%" }}>
        <Loader />
      </div>
    );
  }

  return (
    <div className={styles.pageWrapper}>
      <form>
        {!!data?.AdnocId && (
          <Row size={RowSize.Small}>
            <InputValidated
              label={SubcontractorFieldLabel.COMPANY_ID}
              placeholder="Type here"
              type="string"
              value={getValues(SubcontractorField.COMPANY_ID)}
              onChange={e =>
                handleDataChange(e.target.value, SubcontractorField.COMPANY_ID)
              }
              errorMessage={
                errors[SubcontractorField.COMPANY_ID]?.message as string
              }
              disabled={true}
              mandatory
            />
          </Row>
        )}

        <Row size={RowSize.Small}>
          <InputValidated
            label={SubcontractorFieldLabel.ARABIC_NAME}
            subLabel={SubcontractorFieldSubLabel.ARABIC_NAME}
            placeholder="Type here"
            type="string"
            value={getValues(SubcontractorField.ARABIC_NAME)}
            onChange={e =>
              handleDataChange(e.target.value, SubcontractorField.ARABIC_NAME)
            }
            errorMessage={
              errors[SubcontractorField.ARABIC_NAME]?.message as string
            }
            disabled={isDisabledForm}
            mandatory
          />
        </Row>

        <Row size={RowSize.Small}>
          <InputValidated
            label={SubcontractorFieldLabel.ENGLISH_NAME}
            placeholder="Type here"
            type="string"
            value={getValues(SubcontractorField.ENGLISH_NAME)}
            onChange={e =>
              handleDataChange(e.target.value, SubcontractorField.ENGLISH_NAME)
            }
            errorMessage={
              errors[SubcontractorField.ENGLISH_NAME]?.message as string
            }
            disabled={isDisabledForm}
            mandatory
          />
        </Row>

        <Row size={RowSize.Small}>
          <InputValidated
            label={SubcontractorFieldLabel.CICPA_ID}
            placeholder="Type here"
            type="text"
            value={getValues(SubcontractorField.CICPA_ID)}
            onChange={e => {
              const isValid = checkIfStringAndDash(e.target.value);

              if (isValid) {
                handleDataChange(e.target.value, SubcontractorField.CICPA_ID);
              }
            }}
            errorMessage={
              errors[SubcontractorField.CICPA_ID]?.message as string
            }
            disabled={isDisabledForm}
            mandatory
          />
        </Row>

        <Row size={RowSize.Small} className={styles.spaceBeetween}>
          <Column>
            <InputValidated
              label={SubcontractorFieldLabel.PHONE}
              placeholder="Type here"
              type="text"
              value={getValues(SubcontractorField.PHONE)}
              onChange={e => {
                const isValid = checkIfStringDashPlus(e.target.value);

                if (isValid) {
                  handleDataChange(e.target.value, SubcontractorField.PHONE);
                }
              }}
              errorMessage={errors[SubcontractorField.PHONE]?.message as string}
              mandatory
              disabled={isDisabledForm}
            />
          </Column>

          {!!data?.AdnocId && (
            <Column>
              <InputValidated
                label={SubcontractorFieldLabel.UNIFIED_ID}
                placeholder="Type here"
                type="number"
                value={getValues(SubcontractorField.UNIFIED_ID) ?? ""}
                onChange={e =>
                  handleDataChange(
                    e.target.value,
                    SubcontractorField.UNIFIED_ID,
                  )
                }
                errorMessage={
                  errors[SubcontractorField.UNIFIED_ID]?.message as string
                }
                mandatory
                disabled={isDisabledForm}
              />
            </Column>
          )}
        </Row>

        <Row size={RowSize.Small} className={styles.spaceBeetween}>
          <Column>
            <InputValidated
              label={SubcontractorFieldLabel.ADDCI}
              placeholder="Type here"
              type="string"
              value={getValues(SubcontractorField.ADDCI)}
              onChange={e =>
                handleDataChange(e.target.value, SubcontractorField.ADDCI)
              }
              errorMessage={errors[SubcontractorField.ADDCI]?.message as string}
              mandatory
              disabled={isDisabledForm}
            />
          </Column>
          <Column>
            <ValidationWrapper
              errorMessage={
                errors[SubcontractorField.TRADE_LICENSE_EXPIRY]
                  ?.message as string
              }
            >
              <DatePicker
                wrapperClassName={styles.datePicker}
                labelClassName={styles.datePickerLabel}
                mode="single"
                inputLabel={SubcontractorFieldLabel.TRADE_LICENSE_EXPIRY}
                inputPlaceholder={DEFAULT_DATE_FORMAT}
                selected={getValues(SubcontractorField.TRADE_LICENSE_EXPIRY)}
                onSelectedDateChange={date => {
                  handleDataChange(
                    date,
                    SubcontractorField.TRADE_LICENSE_EXPIRY,
                  );
                }}
                floatingOptions={{
                  strategy: "fixed",
                }}
                defaultMonth={
                  getValues(SubcontractorField.TRADE_LICENSE_EXPIRY) ||
                  new Date()
                }
                mandatory
                isInputDisabled={isDisabledForm}
              />
            </ValidationWrapper>
          </Column>
        </Row>

        <Row size={RowSize.Small}>
          <InputFileDropdown
            label={SubcontractorFieldLabel.TRADE_LICENSE}
            placeholder="Upload Trade Licence"
            requiredOnly={data?.TradeLicenseDocument?.Format}
            onChange={e =>
              onUploadDocument(e, SubcontractorField.TRADE_LICENSE)
            }
            errorMessage={
              errors[SubcontractorField.TRADE_LICENSE]?.message as string
            }
            mandatory
            disabled={isDisabledForm}
          />
        </Row>
      </form>

      {size(compact([attachment])) && (
        <FileList
          file={attachment}
          fileId={attachment?.[0]?.id}
          fileName={attachmentName[0]}
          fileFormat={attachmentFormat[0]}
          onRemove={handleRemoveDocument}
          disabled={isDisabledForm}
          fileItemNameClassName={styles.fileItemName}
        />
      )}

      <Row size={RowSize.ExtraLarge}>
        <Divider />
      </Row>
      <Row size={RowSize.Small} className={styles.flexEnd}>
        {isEdit ? (
          <Button
            className={styles.actionButton}
            variant={ButtonVariant.Negative}
            size={ButtonSize.Small}
            onClick={onDelete}
            disabled={isDisabledForm || isSubmitting}
          >
            Delete
          </Button>
        ) : (
          <Button
            className={styles.actionButton}
            variant={ButtonVariant.Negative}
            size={ButtonSize.Small}
            onClick={onCancel}
          >
            Cancel
          </Button>
        )}

        <Button
          className={styles.actionButton}
          variant={ButtonVariant.Primary}
          size={ButtonSize.Small}
          onClick={handleSubmit(onSubmit)}
          disabled={isDisabledForm || !isValid || isSubmitting}
        >
          Save
        </Button>
      </Row>
    </div>
  );
};
